import './defines';
import analytics from 'controllers/analytics';
import * as PI from 'interfaces/project';
import {
	TemplateSet,
} from 'interfaces/app';
import {
	Component,
	Prop,
	Vue,
	Ref,
} from 'vue-property-decorator';
import TemplatePickerItemView from 'views/template-picker-item';
import { watch as getViewportWatcher } from 'tools/get-viewport';
import Template from './template.vue';

interface TemplateCategory {
	id: string;
	name: string;
	className?: string;
}

@Component({
	components: {
		TemplatePickerItemView,
	},
})
export default class TemplatePickerView extends Vue.extend(Template) {
	@Prop({
		description: 'Text to display on the template',
		required: false,
		type: String,
		default: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla iaculis eu ligula et consequat. In hac habitasse platea dictumst. Sed felis eros, tristique vitae leo eget, ultricies convallis nulla. Ut ut arcu aliquam, mollis odio quis, faucibus lacus. Nulla nunc nisl, eleifend molestie velit eu, ultricies mollis nibh. Sed viverra ante id purus fermentum vestibulum. Sed lobortis gravida blandit. Curabitur vitae massa velit. Integer ut urna efficitur, dapibus risus vel, semper sapien. Nam ex tortor, ullamcorper vitae ultricies id, malesuada in lorem. Nulla nisl nunc, volutpat et lectus nec, elementum sodales elit. Maecenas iaculis, risus ac scelerisque egestas, nibh felis pretium mauris, sit amet hendrerit est sem id lectus. Nullam congue ante sed elit interdum, quis fermentum augue venenatis.\n\nVivamus molestie purus eu dui dapibus, vel semper arcu accumsan. Praesent porttitor ligula ante. Integer imperdiet efficitur gravida. Vestibulum non tellus dignissim, consequat est vitae, placerat dolor. Aliquam molestie finibus dui sed viverra. Phasellus elit sem, scelerisque non nunc non, elementum fermentum ex. Vestibulum elementum urna quis diam faucibus rhoncus. Sed sodales consequat dolor, quis maximus neque condimentum ut. Integer a posuere purus. Suspendisse lorem purus, efficitur eu nibh non, consequat eleifend quam. Aliquam semper lacinia congue. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae;\n\nSuspendisse lacus odio, tempor nec pulvinar sit amet, commodo et nisi. Vestibulum id accumsan lorem. Vestibulum massa odio, porta quis facilisis et, elementum at leo. Maecenas dignissim turpis leo, at luctus massa tempor sed. Maecenas ullamcorper, diam vel posuere rutrum, nisl justo ultricies sem, et mattis purus ante sed lectus. Morbi turpis est, sodales sit amet nulla quis, ultricies blandit lacus. Pellentesque auctor sodales augue. Ut nec metus nec lacus scelerisque lobortis. Mauris euismod lectus libero, in luctus metus interdum',
	})
	public readonly defaultText!: string;

	@Prop({
		description: 'Height of the page the template is for',
		required: true,
		type: Number,
	})
	public readonly pageHeight!: number;

	@Prop({
		description: 'Width of the page the template is for',
		required: true,
		type: Number,
	})
	public readonly pageWidth!: number;

	@Prop({
		description: 'Templates definitions',
		required: true,
		schema: 'TemplateSet',
		type: Array,
	})
	public readonly templateSets!: TemplateSet[];

	@Prop({
		description: 'Id of the selected template set',
		required: false,
		type: String,
	})
	public readonly templateSetId!: PI.PageModel['templateSetId'];

	@Ref('picker')
	private readonly picker!: HTMLOListElement;

	protected categoryId: TemplateCategory['id'] = '';

	private getViewportUnwatch?: () => void;

	protected maxItemHeight = 200;

	protected maxItemWidth = 200;

	protected beforeDestroy(): void {
		this.getViewportUnwatch?.();
		this.getViewportUnwatch = undefined;
	}

	protected mounted(): void {
		this.$nextTick(() => {
			this.setMaxItemWidth();
		});

		this.getViewportUnwatch = getViewportWatcher(() => {
			this.setMaxItemWidth();
		});
	}

	protected close(): void {
		this.$emit('closeDialog');
	}

	protected get categories(): TemplateCategory[] {
		const categories = [];

		if (this.templateSetsWithText.length) {
			categories.push({
				id: 'withText',
				name: this.$t('views.templatePicker.filters.withText'),
			});
		}
		if (this.templateSetsWithOnePhoto.length) {
			categories.push({
				id: 'withOnePhoto',
				name: this.$t('views.templatePicker.filters.onePhoto'),
				class: 'with-photo-icon',
			});
		}
		if (this.templateSetsWithTwoPhotos.length) {
			categories.push({
				id: 'withTwoPhotos',
				name: this.$t('views.templatePicker.filters.twoPhotos'),
				class: 'with-photo-icon',
			});
		}
		if (this.templateSetsWithThreePhotos.length) {
			categories.push({
				id: 'withThreePhotos',
				name: this.$t('views.templatePicker.filters.threePhotos'),
				class: 'with-photo-icon',
			});
		}
		if (this.templateSetsWithFourPhotos.length) {
			categories.push({
				id: 'withFourPhotos',
				name: this.$t('views.templatePicker.filters.fourPhotos'),
				class: 'with-photo-icon',
			});
		}
		if (this.templateSetsWithFivePlusPhotos.length) {
			categories.push({
				id: 'withFivePlusPhotos',
				name: this.$t('views.templatePicker.filters.fivePlusPhotos'),
				class: 'with-photo-icon',
			});
		}

		if (categories.length) {
			categories.unshift({
				id: '',
				name: this.$t('views.templatePicker.filters.all'),
			});
		}

		return categories;
	}

	protected get filteredTemplateSets(): TemplateSet[] {
		if (this.categoryId === 'withText') {
			return this.templateSetsWithText;
		}
		if (this.categoryId === 'withOnePhoto') {
			return this.templateSetsWithOnePhoto;
		}
		if (this.categoryId === 'withTwoPhotos') {
			return this.templateSetsWithTwoPhotos;
		}
		if (this.categoryId === 'withThreePhotos') {
			return this.templateSetsWithThreePhotos;
		}
		if (this.categoryId === 'withFourPhotos') {
			return this.templateSetsWithFourPhotos;
		}
		if (this.categoryId === 'withFivePlusPhotos') {
			return this.templateSetsWithFivePlusPhotos;
		}

		return this.templateSets;
	}

	private get templateSetsWithText(): TemplateSet[] {
		return this.templateSets.filter(
			(templateSet) => {
				const findTextPosition = templateSet.positions.find(
					(position) => position.type === 'text',
				);
				return Boolean(findTextPosition);
			},
		);
	}

	private get templateSetsWithOnePhoto(): TemplateSet[] {
		return this.templateSets.filter(
			(templateSet) => {
				const photoPositions = templateSet.positions.filter(
					(position) => position.type === 'photo',
				);
				return photoPositions.length === 1;
			},
		);
	}

	private get templateSetsWithTwoPhotos(): TemplateSet[] {
		return this.templateSets.filter(
			(templateSet) => {
				const photoPositions = templateSet.positions.filter(
					(position) => position.type === 'photo',
				);
				return photoPositions.length === 2;
			},
		);
	}

	private get templateSetsWithThreePhotos(): TemplateSet[] {
		return this.templateSets.filter(
			(templateSet) => {
				const photoPositions = templateSet.positions.filter(
					(position) => position.type === 'photo',
				);
				return photoPositions.length === 3;
			},
		);
	}

	private get templateSetsWithFourPhotos(): TemplateSet[] {
		return this.templateSets.filter(
			(templateSet) => {
				const photoPositions = templateSet.positions.filter(
					(position) => position.type === 'photo',
				);
				return photoPositions.length === 4;
			},
		);
	}

	private get templateSetsWithFivePlusPhotos(): TemplateSet[] {
		return this.templateSets.filter(
			(templateSet) => {
				const photoPositions = templateSet.positions.filter(
					(position) => position.type === 'photo',
				);
				return photoPositions.length >= 5;
			},
		);
	}

	protected selectCategory(
		categoryId: TemplateCategory['id'],
	): void {
		this.categoryId = categoryId;
	}

	protected selectTemplate(
		templateSetId: string,
	): void {
		analytics.trackEvent(
			'Change Template',
			{
				category: 'Button',
			},
		);

		this.$emit(
			'changeTemplate',
			templateSetId,
		);
	}

	private setMaxItemWidth(): void {
		const computedStyle = window.getComputedStyle(
			this.picker,
		);
		const width = parseInt(
			computedStyle.width,
			10,
		) - 20;
		this.maxItemWidth = width / 2;
	}
}
